import { useEffect } from "react";

/**
 * Scrolls a section into view when the URL hash matches its id, landing
 * just below the header (the target element's CSS must set
 * `scroll-margin-top: var(--header-height, ...)` for the offset to be correct).
 *
 * Homepage/landing sections are often mounted lazily (IntersectionObserver-driven
 * cascades, dynamic imports), so sibling sections can still be loading and
 * shifting layout right after this section appears. A single scrollIntoView
 * call can therefore land short or overshoot past the header. This hook
 * re-issues the scroll a few times as that settles, and stops the moment the
 * visitor scrolls manually so it never fights their intent.
 */
export function useScrollToHashSection(
  ref: React.RefObject<HTMLElement>,
  hash: string,
  retryDelays: number[] = [0, 150, 500, 1000, 1800]
) {
  useEffect(() => {
    if (typeof window === "undefined") return;

    const targetHash = hash.startsWith("#") ? hash : `#${hash}`;
    let timers: ReturnType<typeof setTimeout>[] = [];

    const cancelPending = () => {
      timers.forEach(clearTimeout);
      timers = [];
    };

    const scrollToSection = () => {
      if (window.location.hash !== targetHash || !ref.current) return;

      cancelPending();
      retryDelays.forEach((delay) => {
        timers.push(
          setTimeout(() => {
            if (window.location.hash !== targetHash) return;
            ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
          }, delay)
        );
      });
    };

    window.addEventListener("hashchange", scrollToSection);
    window.addEventListener("wheel", cancelPending, { passive: true });
    window.addEventListener("touchmove", cancelPending, { passive: true });
    scrollToSection();

    return () => {
      cancelPending();
      window.removeEventListener("hashchange", scrollToSection);
      window.removeEventListener("wheel", cancelPending);
      window.removeEventListener("touchmove", cancelPending);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hash]);
}
